import { useState, useRef, useEffect } from 'react';
import { useShare } from '@/hooks/useShare';

interface SocialShareMenuProps {
  url: string;
  title: string;
  description?: string;
  onClose?: () => void;
}

interface SharePlatform {
  name: string;
  icon: React.ReactNode;
  getShareUrl: (url: string, title: string, description?: string) => string;
  color: string;
}

const sharePlatforms: SharePlatform[] = [
  {
    name: 'X',
    icon: (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    getShareUrl: (url, title) =>
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    color: 'hover:bg-gray-800 hover:text-white dark:hover:bg-white dark:hover:text-gray-900',
  },
  {
    name: 'Facebook',
    icon: (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
    getShareUrl: (url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    color: 'hover:bg-[#1877F2] hover:text-white',
  },
  {
    name: 'LinkedIn',
    icon: (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
    getShareUrl: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    color: 'hover:bg-[#0A66C2] hover:text-white',
  },
];

export function SocialShareMenu({
  url,
  title,
  description,
  onClose,
}: SocialShareMenuProps) {
  const { copyToClipboard, copied } = useShare();
  const menuRef = useRef<HTMLDivElement>(null);
  const [copiedLocal, setCopiedLocal] = useState(false);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose?.();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handlePlatformClick = (platform: SharePlatform) => {
    const shareUrl = platform.getShareUrl(url, title, description);
    window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=400');
    onClose?.();
  };

  const handleCopyLink = async () => {
    const success = await copyToClipboard(url);
    if (success) {
      setCopiedLocal(true);
      setTimeout(() => {
        setCopiedLocal(false);
        onClose?.();
      }, 1500);
    }
  };

  const displayCopied = copiedLocal || copied;

  return (
    <div
      ref={menuRef}
      className="absolute z-50 mt-2 w-48 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 dark:bg-gray-800 dark:ring-gray-700"
    >
      <div className="py-1">
        <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
          Share via
        </div>
        {sharePlatforms.map((platform) => (
          <button
            key={platform.name}
            type="button"
            onClick={() => handlePlatformClick(platform)}
            className={`flex w-full items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 transition-colors ${platform.color}`}
          >
            {platform.icon}
            <span>{platform.name}</span>
          </button>
        ))}
        <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
        <button
          type="button"
          onClick={handleCopyLink}
          className={`flex w-full items-center gap-3 px-3 py-2 text-sm transition-colors ${
            displayCopied
              ? 'text-green-600 dark:text-green-400'
              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          {displayCopied ? (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          )}
          <span>{displayCopied ? 'Copied!' : 'Copy link'}</span>
        </button>
      </div>
    </div>
  );
}
